import React, { useEffect, useRef, useState } from 'react';
import Button from './Button';
import {
  getBackground,
  removeBackground,
  setBackground
} from '../services/session.service';
import {
  get as getSession,
  partialUpdate as partialUpdateSession
} from '../services/session.service';
import {
  BackgroundResponse,
  BackgroundTypeEnum,
  ColourStyleEnum,
  SetBackgroundRequest,
  FontStyleEnum,
  SessionDetails
} from '../models/api';
import ModalForm from './Modal';
import LoadingSpinner from './LoadingSpinner';
import EventStyleEditor from './EventStyleEditor';
import { ResponseData } from '../services/models';
import Feedback from './Feedback';
import FileUpload from './FileUpload';

type SessionStyleEditorProps = {
  sessionReference: string;
  close: (isSuccessful?: boolean) => void;
};

const SessionStyleEditor: React.FC<SessionStyleEditorProps> = ({
  sessionReference,
  close
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backgroundType, setBackgroundType] = useState<BackgroundTypeEnum>(
    BackgroundTypeEnum.Image
  );
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [existingData, setExistingData] = useState<BackgroundResponse | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileContents, setSelectedFileContents] = useState<
    string | null
  >(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [filePreview, setFilePreview] = useState<string | ArrayBuffer | null>(
    null
  );
  const [fontStyleEnum, setFontStyleEnum] = useState(FontStyleEnum.Classic);
  const [colourStyleEnum, setColourStyleEnum] = useState(
    ColourStyleEnum.Purple
  );

  const inputFile = useRef<HTMLInputElement>(null);

  const handleReset = () => {
    if (inputFile.current) {
      inputFile.current.value = '';
      inputFile.current.type = 'text';
      inputFile.current.type = 'file';
    }
  };

  const storeSessionData = (sessionData: SessionDetails) => {
    setFontStyleEnum(sessionData.fontStyleEnum ?? FontStyleEnum.Classic);
    setColourStyleEnum(sessionData.colourStyleEnum ?? ColourStyleEnum.Purple);
  };

  const get = async () => {
    const [data, sessionData] = await Promise.all([
      getBackground(sessionReference),
      getSession(sessionReference)
    ]);

    if (data.success && sessionData.success && data.data && sessionData.data) {
      setExistingData(data.data);
      storeSessionData(sessionData.data);
      if (
        data.data.backgroundType === BackgroundTypeEnum.None ||
        data.data.backgroundType == BackgroundTypeEnum.Unknown
      ) {
        setBackgroundType(BackgroundTypeEnum.Image);
      } else {
        setBackgroundType(data.data.backgroundType);
      }
      setErrors([]);
    } else {
      setErrors([...data.errors, ...sessionData.errors]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setSelectedFile(null);
    setVideoSrc(null);
    handleReset();
  }, [backgroundType]);

  useEffect(() => {
    get();
  }, []);

  const handleFileChange = (file?: File) => {
    if (file) {
      setSelectedFile(file);
    }
  };

  useEffect(() => {
    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          if (backgroundType === BackgroundTypeEnum.Video) {
            const fileUrl = URL.createObjectURL(selectedFile);
            setVideoSrc(fileUrl);
          } else {
            setFilePreview(reader.result);
          }
          setSelectedFileContents(reader.result.toString().split(',')[1]);
        } else {
          console.log("Can't read that file?");
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  }, [selectedFile]);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setErrors([]);

    setSaving(true);

    let request: SetBackgroundRequest;
    let ignoreBackground = true;
    if (selectedFile && selectedFileContents) {
      request = {
        backgroundType: backgroundType,
        fileContents: selectedFileContents,
        fileName: selectedFile.name,
        useOriginalFile: false
      };
      ignoreBackground = false;
    } else {
      request = {
        backgroundType: backgroundType,
        fileContents: null,
        fileName: null,
        useOriginalFile: true
      };
    }

    let result: ResponseData<BackgroundResponse> | null = null;

    if (!ignoreBackground) {
      result = await setBackground(sessionReference, request);

      if (result.success && result.data) {
        setExistingData(result.data);
      }
    }

    const sessionData = await partialUpdateSession(sessionReference, {
      fontStyleEnum: fontStyleEnum,
      colourStyleEnum: colourStyleEnum
    });

    if (sessionData.success && sessionData.data) {
      storeSessionData(sessionData.data);
    }

    if ((result?.errors?.length ?? 0) > 0 || sessionData.errors?.length > 0) {
      setErrors([...(result?.errors ?? []), ...sessionData.errors]);
      setMessage('');
    } else {
      setMessage('Style updated');
      close(true);
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    setErrors([]);

    setSaving(true);

    const result = await removeBackground(sessionReference);
    if (result.success) {
      setMessage('Background removed');
      setExistingData({
        backgroundBlobName: null,
        backgroundFileName: null,
        hasBackground: false,
        backgroundType: BackgroundTypeEnum.Image
      });
    } else {
      setMessage('');
      setErrors(result.errors);
    }
    setSaving(false);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <ModalForm halfScreen={true}>
      <form onSubmit={handleSave} className="content-form">
        <h2>Event Style</h2>
        <EventStyleEditor
          colourStyleEnum={colourStyleEnum}
          setColourStyleEnum={setColourStyleEnum}
          setFontStyleEnum={setFontStyleEnum}
          fontStyleEnum={fontStyleEnum}
          isCreation={false}
        />
        {/*<label className="title" htmlFor="backgroundTypeSelect">
          Background
        </label>
        <span>
          Choose a background image/video for your session (optional).
        </span>
        <div className="radio-group">
          <select
            id="backgroundTypeSelect"
            value={backgroundType}
            onChange={(e) =>
              setBackgroundType(e.target.value as BackgroundTypeEnum)
            }
          >
            <option
              key={BackgroundTypeEnum.Image}
              value={BackgroundTypeEnum.Image}
            >
              {BackgroundTypeEnum.Image}
            </option>
            <option
              key={BackgroundTypeEnum.Video}
              value={BackgroundTypeEnum.Video}
            >
              {BackgroundTypeEnum.Video}
            </option>
          </select>
        </div>
        <div className="image-input">
          <FileUpload
            accept={
              backgroundType === BackgroundTypeEnum.Image
                ? 'image/*'
                : 'video/*'
            }
            onChange={handleFileChange}
          />
        </div>
        {selectedFile && (
          <div className="image-preview">
            <label>{selectedFile.name}</label>
            <div>
              {backgroundType === BackgroundTypeEnum.Image && (
                <img
                  src={filePreview as string}
                  alt="Selected"
                  height="100px"
                />
              )}
              {backgroundType === BackgroundTypeEnum.Video && videoSrc && (
                <video controls height="100px">
                  <source src={videoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        )}
        {!selectedFile &&
          existingData &&
          existingData.hasBackground &&
          existingData.backgroundBlobName && (
            <div className="image-preview">
              <label>{existingData.backgroundFileName}</label>
              <div>
                {backgroundType === BackgroundTypeEnum.Image && (
                  <img
                    src={`${process.env.REACT_APP_API_URL}/api/images/${existingData.backgroundBlobName.replace(/\//g, '|')}`}
                    alt="Selected"
                    height="100px"
                  />
                )}
                {backgroundType === BackgroundTypeEnum.Video && (
                  <video controls height="100px">
                    <source
                      src={`${process.env.REACT_APP_API_URL}/api/images/${existingData.backgroundBlobName.replace(/\//g, '|')}`}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </div>
          )}
        {existingData?.hasBackground && (
          <div className="button-container single-button">
            <Button
              type="Secondary"
              isLoading={saving}
              isSubmit={false}
              onClick={() => handleDelete()}
            >
              Remove Background
            </Button>
          </div>
        )}*/}
        <div className="button-container">
          <Button isLoading={saving} isSubmit={true}>
            Save
          </Button>
          <Button type="Outline" onClick={() => close()}>
            Close
          </Button>
        </div>
        <Feedback messages={errors} type="error" />
        <Feedback message={message} type="success" />
      </form>
    </ModalForm>
  );
};

export default SessionStyleEditor;
